/**
 * Celestica Switch Database
 *
 * Celestica switches for AI/ML clusters with Broadcom Tomahawk ASICs.
 *
 * Data sources: research/switch-specs.md
 */

import type { SwitchModel } from '../../types/switches';

/**
 * Celestica DS6000 (1.6TbE, Air-Cooled, Tomahawk 6)
 */
export const CELESTICA_DS6000: SwitchModel = {
  id: 'celestica-ds6000',
  name: 'DS6000',
  vendor: 'Broadcom',
  productName: 'Celestica DS6000',
  fabricType: 'Ethernet',
  typicalRoles: ['leaf', 'spine'],
  ports: {
    totalPorts: 64,
    speed: {
      value: 1600,
      unit: 'Gbps',
      displayName: '1.6TbE',
    },
    portType: 'OSFP',
    supportsBreakout: true,
    breakoutOptions: ['64x1.6T', '128x800G', '256x400G', '512x200G'],
  },
  power: {
    typicalWatts: 1500, // Estimate for TH6
    maxWatts: 2400,
    idleWatts: 600,
    perPortWatts: 18,
    psuConfig: {
      count: 2,
      capacity: 3000,
      redundancy: 'N+1',
    },
  },
  physical: {
    rackUnits: 3,
    widthMm: 438,
    depthMm: 700,
    weightKg: 30,
    airflow: 'front-to-back',
    maxBtuPerHour: 8190,
  },
  performance: {
    switchingCapacityTbps: 102.4,
    forwardingRateMpps: 76800,
    latencyNs: 350,
    bufferSizeMB: 256,
  },
  features: {
    layer2: true,
    layer3: true,
    vxlan: true,
    evpn: true,
    rdma: true,
    adaptiveRouting: true,
    telemetry: true,
    inBandManagement: true,
    oobManagement: true,
    sonic: true,
    additionalFeatures: [
      'Broadcom Tomahawk 6 ASIC',
      'LPO/LRO optics support',
      'SONiC (Celestica Solutions)',
      'Air-cooled for standard data centers',
    ],
  },
  metadata: {
    productUrl: 'https://corporate.celestica.com/news-releases/news-release-details/celestica-introduces-new-family-16tbe-data-center-switches-power',
    releaseYear: 2024,
    endOfLife: false,
    notes: 'Next-gen 1.6TbE switch with Tomahawk 6. Air-cooled 3RU design.',
  },
};

/**
 * Celestica DS6001 (1.6TbE, Hybrid-Cooled, Tomahawk 6)
 */
export const CELESTICA_DS6001: SwitchModel = {
  id: 'celestica-ds6001',
  name: 'DS6001',
  vendor: 'Broadcom',
  productName: 'Celestica DS6001',
  fabricType: 'Ethernet',
  typicalRoles: ['leaf', 'spine'],
  ports: {
    totalPorts: 64,
    speed: {
      value: 1600,
      unit: 'Gbps',
      displayName: '1.6TbE',
    },
    portType: 'OSFP',
    supportsBreakout: true,
    breakoutOptions: ['64x1.6T', '128x800G', '256x400G', '512x200G'],
  },
  power: {
    typicalWatts: 1400, // Slightly better cooling efficiency
    maxWatts: 2300,
    idleWatts: 580,
    perPortWatts: 18,
    psuConfig: {
      count: 2,
      capacity: 2800,
      redundancy: 'N+1',
    },
  },
  physical: {
    rackUnits: 2, // OCP ORv3 21-inch rack (2OU)
    widthMm: 533, // OCP wider form factor
    depthMm: 680,
    weightKg: 28,
    airflow: 'front-to-back',
    maxBtuPerHour: 7850,
  },
  performance: {
    switchingCapacityTbps: 102.4,
    forwardingRateMpps: 76800,
    latencyNs: 350,
    bufferSizeMB: 256,
  },
  features: {
    layer2: true,
    layer3: true,
    vxlan: true,
    evpn: true,
    rdma: true,
    adaptiveRouting: true,
    telemetry: true,
    inBandManagement: true,
    oobManagement: true,
    sonic: true,
    additionalFeatures: [
      'Broadcom Tomahawk 6 ASIC',
      'Hybrid cooling solution',
      'OCP ORv3 21-inch rack optimized',
      'LPO/LRO support',
    ],
  },
  metadata: {
    productUrl: 'https://corporate.celestica.com/news-releases/news-release-details/celestica-introduces-new-family-16tbe-data-center-switches-power',
    releaseYear: 2024,
    endOfLife: false,
    notes: 'Hybrid-cooled variant for OCP ORv3 racks. More compact 2OU design.',
  },
};

/**
 * Celestica DS5000 (800G, Tomahawk 4)
 */
export const CELESTICA_DS5000: SwitchModel = {
  id: 'celestica-ds5000',
  name: 'DS5000',
  vendor: 'Broadcom',
  productName: 'Celestica DS5000',
  fabricType: 'Ethernet',
  typicalRoles: ['leaf', 'spine'],
  ports: {
    totalPorts: 64,
    speed: {
      value: 800,
      unit: 'Gbps',
      displayName: '800GbE',
    },
    portType: 'OSFP',
    supportsBreakout: true,
    breakoutOptions: ['64x800G', '128x400G', '256x200G', '512x100G'],
  },
  power: {
    typicalWatts: 950,
    maxWatts: 1600,
    idleWatts: 400,
    perPortWatts: 12,
    psuConfig: {
      count: 2,
      capacity: 2000,
      redundancy: 'N+1',
    },
  },
  physical: {
    rackUnits: 2,
    widthMm: 438,
    depthMm: 600,
    weightKg: 20,
    airflow: 'front-to-back',
    maxBtuPerHour: 5460,
  },
  performance: {
    switchingCapacityTbps: 51.2,
    forwardingRateMpps: 38400,
    latencyNs: 320,
    bufferSizeMB: 128,
  },
  features: {
    layer2: true,
    layer3: true,
    vxlan: true,
    evpn: true,
    rdma: true,
    adaptiveRouting: true,
    telemetry: true,
    inBandManagement: true,
    oobManagement: true,
    sonic: true,
    additionalFeatures: [
      'High-performance processor (up to 8 cores)',
      'SONiC support',
      'Ethernet-based fabric',
    ],
  },
  metadata: {
    productUrl: 'https://www.celestica.com/blog/article/celesticas-ai-ml-data-center-network-switch-ds5000-is-now-shipping',
    releaseYear: 2023,
    endOfLife: false,
    notes: '800G switch with Tomahawk 4. Multi-speed port support.',
  },
};

/**
 * Celestica DS4100 (800G, Compact 1U)
 */
export const CELESTICA_DS4100: SwitchModel = {
  id: 'celestica-ds4100',
  name: 'DS4100',
  vendor: 'Broadcom',
  productName: 'Celestica DS4100',
  fabricType: 'Ethernet',
  typicalRoles: ['leaf'],
  ports: {
    totalPorts: 16,
    speed: {
      value: 800,
      unit: 'Gbps',
      displayName: '800GbE',
    },
    portType: 'OSFP',
    supportsBreakout: true,
    breakoutOptions: ['16x800G', '32x400G', '64x200G'],
  },
  power: {
    typicalWatts: 300,
    maxWatts: 500,
    idleWatts: 150,
    perPortWatts: 12,
    psuConfig: {
      count: 1,
      capacity: 600,
      redundancy: 'N',
    },
  },
  physical: {
    rackUnits: 1,
    widthMm: 438,
    depthMm: 450,
    weightKg: 8,
    airflow: 'front-to-back',
    maxBtuPerHour: 1706,
  },
  performance: {
    switchingCapacityTbps: 12.8,
    forwardingRateMpps: 9600,
    latencyNs: 300,
    bufferSizeMB: 32,
  },
  features: {
    layer2: true,
    layer3: true,
    vxlan: true,
    evpn: true,
    rdma: true,
    adaptiveRouting: false,
    telemetry: true,
    inBandManagement: true,
    oobManagement: true,
    sonic: true,
    additionalFeatures: [
      'Broadcom TH4-12.8T chipset',
      'Compact 1U design',
      'Optimized for AI/ML',
    ],
  },
  metadata: {
    productUrl: 'https://corporate.celestica.com/news-releases/',
    releaseYear: 2023,
    endOfLife: false,
    notes: 'Compact 16-port 800G switch for small-scale AI workloads. 1U form factor.',
  },
};

/**
 * All Celestica switches
 */
export const CELESTICA_SWITCHES: readonly SwitchModel[] = [
  CELESTICA_DS6000,
  CELESTICA_DS6001,
  CELESTICA_DS5000,
  CELESTICA_DS4100,
] as const;

/**
 * Get Celestica switch by ID
 */
export function getCelesticaSwitch(id: string): SwitchModel | undefined {
  return CELESTICA_SWITCHES.find((sw) => sw.id === id);
}
